import React, { useState, useEffect } from 'react';
import './CustomerHistoryModal.css';
import QueueService from './QueueService';

const MappingDetailsList = ({ visitId, type, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTranscript, setShowTranscript] = useState({});
  const [showMoreOrderDetails, setShowMoreOrderDetails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (type === 'calls') {
          const result = await QueueService.fetchCallsForMapping(visitId, token);
          setData(result);
        } else if (type === 'order') {
          const result = await QueueService.fetchOrderForMapping(visitId, token);
          setData(result);
        }
      } catch (err) {
        setError(`Failed to fetch ${type} details`);
      } finally {
        setLoading(false);
      }
    };
    if (visitId && type) {
      fetchData();
    }
  }, [visitId, type, token]);

  if (loading) return <div className="mapping-details-loading">Loading...</div>;
  if (error) return <div className="mapping-details-error">{error}</div>;
  if (!data || (Array.isArray(data) && data.length === 0)) return <div className="mapping-details-empty">No {type} found</div>;

  if (type === 'calls') {
    const callsArray = Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : []);
    
    if (callsArray.length === 0) {
      if (typeof data === 'object' && !Array.isArray(data)) {
        return (
           <div className="mapping-details-empty" style={{textAlign: 'left', fontSize: '11px'}}>
              <strong>Dev Info (Data is an object):</strong> <br/>
              <pre>{JSON.stringify(data, null, 2)}</pre>
           </div>
        );
      }
      return <div className="mapping-details-empty">No calls found</div>;
    }

    return (
      <div className="mapping-calls">
        {callsArray.map((call, idx) => {
          const isComing = call.user_response === "1";
          const isNotComing = call.user_response === "2";
          
          return (
          <div key={idx} className="chm-call-card">
            <div className="chm-call-header">
              <span className="chm-call-date">
                <i className="far fa-clock"></i> {call.created_at ? new Date(call.created_at).toLocaleString() : 'N/A'}
              </span>
              <span className={`chm-call-status chm-status-${(call.status || '').toLowerCase()}`}>{call.status}</span>
            </div>
            <div className="chm-call-body">
              <div className="chm-call-info-block">
                <span className="chm-call-label">Response</span>
                <span className={`chm-call-value ${isComing ? 'resp-coming' : isNotComing ? 'resp-not-coming' : ''}`}>
                  {isComing ? "Coming" : isNotComing ? "Not Coming" : call.user_response || "N/A"}
                </span>
              </div>
              <div className="chm-call-info-block">
                <span className="chm-call-label">ETA</span>
                <span className="chm-call-value">{call.user_arrival_time || 'N/A'}</span>
              </div>
              <div className="chm-call-info-block">
                <span className="chm-call-label">Answer Time</span>
                <span className="chm-call-value">{call.answer_time != null ? `${call.answer_time}s` : 'N/A'}</span>
              </div>
              <div className="chm-call-info-block">
                <span className="chm-call-label">Source</span>
                <span className="chm-call-value" style={{ display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'capitalize' }}>
                  {call.source?.toLowerCase() === 'whatsapp' && <i className="fab fa-whatsapp" style={{ color: '#25D366', fontSize: '1rem' }}></i>}
                  {call.source || 'Unknown'}
                </span>
              </div>
            </div>
            {call.call_transcript && call.call_transcript.length > 0 && (
              <div className="transcript-section">
                <button 
                   className="transcript-toggle-btn"
                   onClick={() => setShowTranscript(prev => ({...prev, [call.id]: !prev[call.id]}))}
                >
                   {showTranscript[call.id] ? "Hide Transcript" : "Show Transcript"}
                </button>
                {showTranscript[call.id] && (
                  <div className="transcript-box">
                    {call.call_transcript.map((msg) => (
                      <div key={msg.id} className={`transcript-msg ${msg.role}`}>
                        <strong>{msg.role === 'assistant' ? 'AI' : 'User'}:</strong> {msg.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    );
  }

  if (type === 'order') {
    // Determine order details safely
    const orderObj = Array.isArray(data) ? data[0] : (data?.data || data || {});
    if (!orderObj || Object.keys(orderObj).length === 0) {
      return <div className="mapping-details-empty">No order details found</div>;
    }

    const orderTypeMap = {
      'D': 'Dine-in',
      'DINE_IN': 'Dine-in',
      'DINEIN': 'Dine-in',
      'T': 'Pickup',
      'P': 'Pickup',
      'PICKUP': 'Pickup',
      'PARCEL': 'Pickup',
      'TAKEAWAY': 'Pickup',
      'L': 'Home Delivery',
      'H': 'Home Delivery',
      'HOME_DELIVERY': 'Home Delivery',
      'DELIVERY': 'Home Delivery'
    };
    const orderTypeLabel = orderTypeMap[orderObj.order_type?.toUpperCase()] || orderObj.order_type || 'Unknown';
    let locationLabel = '';
    if (orderObj.order_type === 'D') {
      locationLabel = `Table: #${orderObj.table_no || 'N/A'}`;
    } else {
      locationLabel = orderObj.address ? 'Delivery' : 'Pickup';
    }

    let subtotal = 0;
    const items = orderObj.product_items?.product_items || [];
    subtotal = items.reduce((acc, item) => acc + ((item.item_price || item.final_price || 0) * (item.quantity || 1)), 0);

    const discountReason = orderObj.discount_details?.details?.[0]?.title || '';
    const discountVal = orderObj.discount_total || 0;
    const taxVal = orderObj.tax_total || 0;
    
    // Explicit formula requested: Total Amount = total_amount - discount + tax
    const finalTotalAmount = (Number(orderObj.total_amount) || 0) - discountVal + taxVal;

    return (
      <div className="chm-order-wrapper">
         <div className="chm-order-summary">
            {/* 1. Order String */}
            <div className="chm-os-section">
              <h4 className="chm-os-title">Order Items</h4>
              {items.length > 0 ? items.map((item, idx) => {
                 const addOns = item.addon_items && item.addon_items.length > 0 
                   ? `(${item.addon_items.map(a => a.name).join(', ')})` 
                   : '';
                 return (
                   <div key={idx} className="chm-os-item">
                     <span>{item.quantity || 1} x {item.product_name} <em>{addOns}</em></span>
                     <span className="chm-os-price">— ₹{item.final_price || item.item_price || 0}</span>
                   </div>
                 );
              }) : <p className="chm-os-text">No items recorded.</p>}
            </div>

            {/* 2. Service Type & Location */}
            <div className="chm-os-section">
              <h4 className="chm-os-title">Service Type & Location</h4>
              <p className="chm-os-text"><strong>{orderTypeLabel}:</strong> {locationLabel}</p>
            </div>

            {/* 3. Financial Breakdown */}
            <div className="chm-os-section chm-os-financials">
              <div className="chm-os-row">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {discountVal > 0 && (
                <div className="chm-os-row chm-os-discount">
                  <span>Discount: {discountReason ? `"${discountReason}"` : ''}</span>
                  <span>— ₹{discountVal.toFixed(2)}</span>
                </div>
              )}
              {orderObj.tax_total > 0 && (
                <div className="chm-os-row">
                  <span>Tax:</span>
                  <span>+ ₹{orderObj.tax_total.toFixed(2)}</span>
                </div>
              )}
              <div className="chm-os-row chm-os-total">
                <span>Total Amount:</span>
                <span>₹{finalTotalAmount.toFixed(2)}</span>
              </div>
            </div>
         </div>

         <button 
           className="chm-order-toggle-btn"
           onClick={() => setShowMoreOrderDetails(!showMoreOrderDetails)}
         >
            {showMoreOrderDetails ? 'Hide Detailed View' : 'Show More Details'}
         </button>

         {/* Detailed Format */}
         {showMoreOrderDetails && (
           <div className="chm-order-details-dump">
             <div className="chm-od-row"><strong>Type:</strong> <span>{orderTypeMap[orderObj.order_type?.toUpperCase()] || orderObj.order_type}</span></div>
             <div className="chm-od-row"><strong>Status:</strong> <span>{orderObj.status}</span></div>
             <div className="chm-od-row"><strong>Table No:</strong> <span>{orderObj.table_no || 'N/A'}</span></div>
             <div className="chm-od-row"><strong>Source:</strong> <span>{orderObj.source || 'N/A'}</span></div>
             <div className="chm-od-row"><strong>POS Invoice:</strong> <span>{orderObj.pos_invoice_number || 'N/A'}</span></div>
             <div className="chm-od-row"><strong>Customer Name:</strong> <span>{orderObj.customer_name || 'N/A'}</span></div>
             
             {orderObj.tax_details?.details && orderObj.tax_details.details.length > 0 && (
               <div className="chm-od-section">
                 <strong>Tax Details:</strong>
                 <ul>
                   {orderObj.tax_details.details.map((tax, i) => (
                     <li key={i}>{tax.title} — ₹{tax.price}</li>
                   ))}
                 </ul>
               </div>
             )}
           </div>
         )}
      </div>
    );
  }

  return null;
};

const CustomerHistoryModal = ({ customerId, token, onClose }) => {
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [activeDetails, setActiveDetails] = useState({});

  const toggleDetails = (visitId, type) => {
    setActiveDetails(prev => ({
       ...prev,
       [visitId]: prev[visitId] === type ? null : type
    }));
  };

  const getDaysAgo = (dateString) => {
    const visitDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - visitDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getMealType = (dateString) => {
    const visitDate = new Date(dateString);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const hours = new Date(visitDate.getTime() + istOffset).getHours();
    // Lunch if between 6:00 and 18:29 in IST, otherwise Dinner
    return hours >= 6 && hours < 18.5 ? 'Lunch' : 'Dinner';
  };

  const handleClickOutside = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };

  const handleDateClick = (visitId) => {
    setSelectedVisit(selectedVisit === visitId ? null : visitId);
  };

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const data = await QueueService.getCustomerHistory(customerId, token);
        setCustomerData(data);
      } catch (error) {
        console.error('Error fetching customer details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomerDetails();
  }, [customerId, token]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={handleClickOutside}>
        <div className="modal">
          <div className="modal-content-history">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="modal-overlay" onClick={handleClickOutside}>
        <div className="modal">
          <div className="modal-content-history">
            <div>No customer details found</div>
          </div>
        </div>
      </div>
    );
  }

  const visits = customerData.visits || [];
  const currentPoints = visits.reduce((total, visit) =>
    total + (visit.loyalty_points_earned || 0) - (visit.loyalty_points_redeemed || 0), 0
  );

  return (
    <div className="modal-overlay" onClick={handleClickOutside}>
      <div className="modal">
        <div className="modal-header">
          <div className="header-left">
            <p className="header-title">
              {customerData.name || 'Customer'} History ({customerData.entered_visits}/{customerData.total_visits} Visits)
            </p>
            {customerData.tags && customerData.tags.length > 0 && (
              <div className="header-tags">
                {customerData.tags.map((tag, index) => (
                  <span key={index} className="customer-tag header-tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose}>

          </button>
        </div>

        <div className="modal-content-history">
          {/* Customer Details Section */}
          <div className="customer-info-container">
            <div className="customer-details-grid">
              <div className="detail-item">
                <span className="detail-label">Phone</span>
                <span className="detail-value">
                  {customerData.country_code ? `+${customerData.country_code} ` : ''}{customerData.phone_number || 'N/A'}
                </span>
              </div>

              <div className="detail-item">
                <span className="detail-label">DOB</span>
                <span className="detail-value">{customerData.dob || '-'}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label">Anniversary</span>
                <span className="detail-value">{customerData.anniversary_date || '-'}</span>
              </div>

              {customerData.sex && (
                <div className="detail-item">
                  <span className="detail-label">Gender</span>
                  <span className="detail-value">{customerData.sex}</span>
                </div>
              )}

              {customerData.marital_status && (
                <div className="detail-item">
                  <span className="detail-label">Marital Status</span>
                  <span className="detail-value">{customerData.marital_status}</span>
                </div>
              )}

              <div className="detail-item">
                <span className="detail-label">Current Points</span>
                <span className="detail-value points-highlight">{currentPoints}</span>
              </div>
            </div>

          </div>

          <div className="visit-history">
            {visits.map((visit) => {
              const daysAgo = getDaysAgo(visit.created_at);
              const isSelected = selectedVisit === visit.id;
              const createdDateIST = new Date(
                new Date(visit.created_at).getTime() + 5.5 * 60 * 60 * 1000
              );
              return (
                <div key={visit.id} className="visit-row">
                  <div
                    className="days-ago"
                    onClick={() => handleDateClick(visit.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span>
                      {daysAgo === 0
                        ? 'Today'
                        : daysAgo === 1
                          ? 'Yesterday'
                          : `${daysAgo} days ago`}{' '}
                      | {getMealType(visit.created_at)}
                      {visit.table_name && (
                        <span> | {visit.table_name}</span>
                      )}
                    </span>
                    {isSelected && (
                      <span className="exact-date-time">
                        {createdDateIST.toLocaleString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: '2-digit',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true,
                        })}
                      </span>
                    )}
                  </div>
                  <hr className="gray-line" />
                  <div className="history-card-details">
                    <div className="2nd-line">
                      <span className="visit-name">
                        <i
                          className="fas fa-map-marker-alt"
                          style={{
                            fontSize: '11px',
                            color: '#4B4B4B',
                            marginRight: '4px',
                          }}
                        ></i>
                        {visit.name
                          ? visit.name.length > 20
                            ? `${visit.name.substring(0, 17)}...`
                            : visit.name
                          : 'N/A'}
                      </span>
                    </div>

                    {/* Status badge */}
                    <div className={`status-indicator ${visit.status}`}>
                      {visit.status}
                    </div>
                  </div>

                  {/* Review section */}
                  <div className="review-container">
                    {/* Display a rating badge if rating is not null */}
                    {visit.review && visit.review.rating != null ? (
                      <div className={`rating-badge rating-${visit.review.rating}`}>
                        {visit.review.rating}
                        <i className="fas fa-star" style={{ marginLeft: '3px' }}></i>
                      </div>
                    ) : (
                      <div className="rating-badge no-rating">---</div>
                    )}

                    {/* Review text if available */}
                    {visit.review && visit.review.text ? (
                      <p className="review-text">{visit.review.text}</p>
                    ) : (
                      <p className="review-text no-review">No Review</p>
                    )}
                  </div>

                  {/* Compact Loyalty and bill information section */}
                  <div className="loyalty-compact-container">
                    <div className="loyalty-pill earned">
                      <span className="pill-label">Earned:</span>
                      <span className="pill-value">+{visit.loyalty_points_earned || 0}</span>
                    </div>
                    <div className="loyalty-pill redeemed">
                      <span className="pill-label">Redeemed:</span>
                      <span className="pill-value">-{visit.loyalty_points_redeemed || 0}</span>
                    </div>
                    <div className="loyalty-pill bill">
                      <span className="pill-label">Bill:</span>
                      <span className="pill-value">₹{visit.total_bill_amount ? visit.total_bill_amount.toFixed(0) : '0'}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {( (visit.initial_waiting_number != null && visit.initial_waiting_number > 0) || (visit.total_bill_amount != null && visit.total_bill_amount > 0) ) && (
                    <div className="action-buttons-container">
                      {visit.initial_waiting_number != null && visit.initial_waiting_number > 0 && (
                        <button 
                           className={`action-btn ${activeDetails[visit.id] === 'calls' ? 'active' : ''}`}
                           onClick={() => toggleDetails(visit.id, 'calls')}
                        >
                          <i className="fas fa-phone"></i> Call History
                        </button>
                      )}
                      {visit.total_bill_amount != null && visit.total_bill_amount > 0 && (
                        <button 
                           className={`action-btn ${activeDetails[visit.id] === 'order' ? 'active' : ''}`}
                           onClick={() => toggleDetails(visit.id, 'order')}
                        >
                          <i className="fas fa-receipt"></i> Order Details
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Dynamic Section */}
                  {activeDetails[visit.id] && (
                     <div className="mapping-dynamic-section">
                       <MappingDetailsList visitId={visit.id} type={activeDetails[visit.id]} token={token} />
                     </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="secondary-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerHistoryModal;
